import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PlayCircle, UserRound, Wind } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useT } from '@/i18n/useT';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * Login is a full-page redirect to Google — no form, no password.
 * Deliberately calm: this is often the first screen of a hard decision.
 * Below it, two public demo affordances: enter as the "Maclet" sandbox account,
 * and walk the sign-up flow without creating anything.
 */
export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  // The sandbox accounts to offer (maclet, beni, …) — data-driven so new ones
  // appear here automatically.
  const { data: demo } = useQuery({
    queryKey: ['auth', 'demo-users'],
    queryFn: () => api<{ users: { username: string; displayName: string }[] }>('/auth/demo-users'),
  });
  const demoUsers = demo?.users ?? [];

  const enterAsDemo = async (username: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await api('/auth/demo', { method: 'POST', body: JSON.stringify({ username }) });
      // Don't reload: a full reload rehydrates the persisted (unauthenticated)
      // auth cache and RequireAuth bounces back to /login before it refetches.
      // Invalidate auth so it refetches the new session, then navigate in-app.
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth() });
      navigate('/', { replace: true });
    } catch {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-between bg-bg px-6 py-12">
      <div className="flex flex-1 flex-col items-center justify-center text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-[28px] bg-brand text-white shadow-lg">
          <Wind size={40} aria-hidden />
        </div>
        <h1 className="text-[32px] font-bold tracking-tight text-content">Respiria</h1>
        <p className="mt-3 max-w-xs text-[15px] text-muted">{t('login.tagline')}</p>
      </div>

      <div className="w-full max-w-xs">
        {/* A plain link, not fetch: OAuth needs a top-level navigation. */}
        <a
          href="/auth/google"
          className="flex min-h-12 w-full items-center justify-center gap-3 rounded-[var(--radius-btn)] bg-surface px-5 text-[15px] font-semibold text-content shadow-[var(--shadow-card)] transition-transform active:scale-[0.98]"
        >
          <GoogleMark />
          {t('login.google')}
        </a>

        {/* Public demo buttons. */}
        <div className="mt-3 space-y-2">
          {demoUsers.map((u) => (
            <button
              key={u.username}
              type="button"
              onClick={() => void enterAsDemo(u.username)}
              disabled={busy}
              className="flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-surface-2 px-5 text-[14px] font-medium text-content transition-transform active:scale-[0.98] disabled:opacity-60"
            >
              <UserRound size={16} aria-hidden />
              {t('login.demoAs', { name: u.displayName })}
            </button>
          ))}
          <button
            type="button"
            onClick={() => navigate('/demo')}
            className="flex min-h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-surface-2 px-5 text-[14px] font-medium text-content transition-transform active:scale-[0.98]"
          >
            <PlayCircle size={16} aria-hidden />
            {t('login.demoRegister')}
          </button>
        </div>

        <p className="mt-4 text-center text-[12px] leading-relaxed text-muted">{t('login.dataNote')}</p>
      </div>
    </div>
  );
}

function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.35 0-4.34-1.58-5.05-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.95 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l2.99-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.99 2.33C4.66 5.16 6.65 3.58 9 3.58Z"
      />
    </svg>
  );
}
